import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { useGetClientCalendarQuery } from 'services/clientApi';
import { IoInformationCircleSharp, IoClose } from 'react-icons/io5';
import { TbClockHour4 } from 'react-icons/tb';
import { FaUser, FaLock, FaShieldAlt } from "react-icons/fa";
import ModalV3 from '../../shared/Modal/ModalV3';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './MyCalendar.scss';

const localizer = momentLocalizer(moment);

export default function MyCalendarComponent({ isMobile, mainProps, showSidebar }) {
    const navigate = useNavigate();
    const { main, mainLoading, mainError } = mainProps;
    const [data, setData] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [openModalMobile, setOpenModalMobile] = useState(false);

    const { data: schedule } = useGetClientCalendarQuery();
    const events = schedule?.schedules?.map(event => ({
        ...event,
        start: moment(event.start).toDate(),
        end: moment(event.end).toDate(),
    }));

    return (
        <div
            className="container-fluid bg-col transition-width"
            style={!isMobile ? {
                width: showSidebar ? 'calc(100% - 332px)' : '100%',
                marginLeft: 0,
                marginRight: 'auto',
                transition: showSidebar ? 'width 0.3s ease-in-out' : 'width 0.16s ease-in-out'
            } : {}}
        >
            <div className="row p-3 reports-panel">
                <h5 className="mb-5 mt-2">MY CALENDAR</h5>
                <p className="text-muted">This calendar will display all your scheduled appointments with our specialists, including meetings, consultations, and other important banking sessions.</p>

                <div className="w-95 m-auto shadow-lg bg-white p-5 mt-5 d-flex gap-3">
                    <div className="calendar-wrapper w-100" style={{ overflowX: "auto" }}>
                        <Calendar
                            localizer={localizer}
                            events={events}
                            eventPropGetter={(event) => {
                                const backgroundColor = event?.status?.color || '#1E266D';
                                return { style: { backgroundColor, color: 'black' } };
                            }}
                            startAccessor="start"
                            endAccessor="end"
                            style={{
                                minWidth: isMobile ? 700 : '100%',
                                height: isMobile ? 600 : 800
                            }}
                            onDoubleClickEvent={(event) => {
                                setData(event);
                                setOpenModal(true);
                            }}
                            onSelectEvent={(event) => {
                                setData(event);
                                setOpenModalMobile(true);
                            }}
                        />
                    </div>

                    {!isMobile && openModal && data && (
                        <div className="event-details-card rounded-4 shadow bg-white overflow-hidden w-70 w-md-40 m-auto mt-4 border position-relative">

                            <IoClose
                                onClick={() => setOpenModal(false)}
                                className="position-absolute text-white"
                                size={24}
                                style={{ top: 10, right: 10, cursor: "pointer", zIndex: 10 }}
                                aria-label="Close"
                            />

                            <div className="eventbg-primary-custom p-4 text-white rounded-top">
                                <h5 className="mb-0 fw-semibold text-center">Event Details</h5>
                            </div>

                            <div className="p-4">
                                <h4 className="text-dark mb-3">{data?.title || "Untitled Event"}</h4>

                                <div className="d-flex align-items-center text-muted mb-4">
                                    <TbClockHour4 size={30} className="text-appointment-primary" />
                                    <span className="ms-2">
                                        {moment(data?.start).format("DD MMM YYYY • HH:mm")} –{" "}
                                        {moment(data?.end).format("HH:mm")}
                                    </span>
                                </div>

                                <hr className="my-4" />

                                <div className="text-section">
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                        <IoInformationCircleSharp size={35} className="text-appointment-primary" />
                                        <h6 className="mb-0 fw-semibold">Description</h6>
                                    </div>
                                    <p className="text-secondary mb-0" style={{ whiteSpace: "pre-line" }}>
                                        {data?.description || "No description available for this event."}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {isMobile && openModalMobile && data && (
                        <ModalV3 isOpen={openModalMobile} onClose={() => setOpenModalMobile(false)}>
                            <div className="p-3">
                                <h5>Event Details</h5>
                                <div className="w97 m-auto my-3" style={{ borderBottom: "2px solid #EFEFEF" }}></div>

                                <div className="p-4">
                                    <h4 className="text-dark mb-3">{data?.title || "Untitled Event"}</h4>

                                    <div className="d-flex align-items-center text-muted mb-4">
                                        <TbClockHour4 size={30} className="text-appointment-primary" />
                                        <span className="ms-2">
                                            {moment(data?.start).format("DD MMM YYYY • HH:mm")} –{" "}
                                            {moment(data?.end).format("HH:mm")}
                                        </span>
                                    </div>

                                    <hr className="my-4" />

                                    <div className="text-section">
                                        <div className="d-flex align-items-center gap-2 mb-3">
                                            <IoInformationCircleSharp size={35} className="text-appointment-primary" />
                                            <h6 className="mb-0 fw-semibold">Description</h6>
                                        </div>
                                        <p className="text-secondary mb-0" style={{ whiteSpace: "pre-line" }}>
                                            {data?.description || "No description available for this event."}
                                        </p>
                                    </div>
                                </div>
                                <div className={`mt-5 ${isMobile ? "text-center" : "text-end"}`}>
                                    <button className={`btn btn-primary ${isMobile ? "w-100" : "w-20"}`} onClick={() => {
                                        setOpenModalMobile(false)
                                    }}>
                                        Done
                                    </button>
                                </div>
                            </div>
                        </ModalV3>
                    )}

                </div>

                {isMobile && (
                    <section className="d-block d-md-none mt-4 px-3 pb-5">
                        <h5 className="text-muted fw-semibold mb-4">You may also need to manage:</h5>
                        <div className="d-grid gap-3">
                            <div
                                onClick={() => navigate("/personal-data")}
                                className="d-flex align-items-center gap-3 p-3 bg-light border rounded-4 shadow-sm cursor-pointer hover-bg"
                            >
                                <FaUser size={25} style={{ color: "#1E266D" }} />
                                <span className="fw-medium text-dark">Personal Data</span>
                            </div>

                            <div
                                onClick={() => navigate("/passwords")}
                                className="d-flex align-items-center gap-3 p-3 bg-light border rounded-4 shadow-sm cursor-pointer hover-bg"
                            >
                                <FaLock size={25} style={{ color: "#1E266D" }} />
                                <span className="fw-medium text-dark">Passwords</span>
                            </div>

                            <div
                                onClick={() => navigate("/privacy-and-consent")}
                                className="d-flex align-items-center gap-3 p-3 bg-light border rounded-4 shadow-sm cursor-pointer hover-bg"
                            >
                                <FaShieldAlt size={25} style={{ color: "#1E266D" }} />
                                <span className="fw-medium text-dark">Privacy and Consent</span>
                            </div>
                        </div>
                    </section>
                )}

            </div>
        </div>
    );
}